import React from 'react';
import { PhotoWithFallback } from '../elements';
import type { Testimonial } from '../../types/api/testimonial';
import { getAvatarUrl } from '../../utils/photo-helpers';

interface TestimonialsGridProps {
  testimonials?: Testimonial[] | null;
  title?: string;
  subtitle?: string;
}

export const TestimonialsGrid = ({
  testimonials: testimonialsData,
  title = "",
  subtitle = "",
}: TestimonialsGridProps) => {
  const testimonials = Array.isArray(testimonialsData) ? testimonialsData : [];

  return (
    <section>
      <div className="mx-auto max-w-container px-4 md:px-8">
        {(title || subtitle) && (
          <div className="mb-12 text-center">
            {title && (
              <h2 className="font-display text-4xl font-normal text-fg-primary md:text-5xl">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="mt-4 font-display text-lg leading-relaxed text-tertiary md:text-xl max-w-3xl mx-auto">
                {subtitle}
              </p>
            )}
          </div>
        )}

        {testimonials.length > 0 ? (
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {testimonials.map((testimonial, i: number) => {
              const quote = testimonial.content_markdown?.replace(/[#*\[\]()]/g, '').trim() || '';
              const reviewerName = testimonial.reviewer_name || 'Customer';
              const username = `@${reviewerName.toLowerCase().replace(/\s+/g, '')}`;
              const avatarUrl = getAvatarUrl(testimonial.photo_attachments, testimonial.id, reviewerName);
              const rating = testimonial.rating || 5;
              const isVerified = true; // Always show verified badge
              
              return (
                <div key={testimonial.id || i} className="bg-white p-8 flex flex-col h-full">
                  <div className="flex gap-1 mb-6">
                    {Array.from({ length: 5 }).map((_, starIdx) => (
                      <svg
                        key={starIdx}
                        className="w-5 h-5"
                        style={{
                          fill: starIdx < rating ? "var(--color-text-brand-secondary)" : "none",
                          stroke: starIdx < rating ? "none" : "#E5E0D8"
                        }}
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    ))}
                  </div>

                  <p className="font-display text-lg leading-relaxed text-tertiary mb-6 flex-grow">
                    &quot;{quote}&quot;
                  </p>

                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                      <PhotoWithFallback
                        photoUrl={avatarUrl}
                        photoAlt={reviewerName}
                        fallbackId={testimonial.id || i}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="font-body text-sm font-medium text-fg-primary">
                          {reviewerName}
                        </p>
                        {isVerified && (
                          <svg className="w-4 h-4 text-fg-primary" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <p className="font-body text-xs" style={{ color: 'var(--color-text-brand-secondary)' }}>{username}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="font-body text-base text-tertiary">No testimonials available</p>
          </div>
        )}
      </div>
    </section>
  );
};

import { registerThemeVariant } from '../../lib/component-registry';
registerThemeVariant('testimonials-grid', 'aman', TestimonialsGrid);

